import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { useAuthStore } from './authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface OrganizationMember {
  id: string;
  user: {
    id: string;
    email?: string;
    display_name: string;
    initials: string;
    avatar_color: string;
  };
  user_email?: string;
  user_id_short?: string;
  teams?: Array<{ id: string; name: string }>;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
}

export interface OrganizationInvite {
  id: string;
  email: string;
  role: 'admin' | 'member';
  invited_by: {
    id: string;
    display_name: string;
    initials: string;
    avatar_color: string;
  };
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  is_expired: boolean;
  is_accepted: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  owner: {
    id: string;
    email?: string;
    display_name: string;
    initials: string;
    avatar_color: string;
  };
  member_count: number;
  team_count: number;
  my_role?: 'owner' | 'admin' | 'member' | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationDetail extends Organization {
  members: OrganizationMember[];
  invites?: OrganizationInvite[];
  teams: any[];
  boards: any[];
  subscription: any;
  my_role?: 'owner' | 'admin' | 'member';
}

interface OrganizationState {
  currentOrganization: OrganizationDetail | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  fetchOrganizations: () => Promise<void>;
  fetchCurrentOrganization: (orgId?: string) => Promise<void>;
  setCurrentOrganization: (org: Organization | null) => void;
  switchOrganization: (orgId: string) => Promise<void>;
  inviteMember: (orgId: string, email: string, role?: 'admin' | 'member') => Promise<void>;
  cancelInvite: (orgId: string, inviteId: string) => Promise<void>;
  resendInvite: (orgId: string, inviteId: string) => Promise<void>;
  removeMember: (orgId: string, userId: string) => Promise<void>;
  updateMemberRole: (orgId: string, userId: string, role: 'owner' | 'admin' | 'member') => Promise<void>;
  transferOwnership: (orgId: string, newOwnerId: string) => Promise<void>;
  addTeamAdmin: (teamId: string, userId: string) => Promise<void>;
  removeTeamAdmin: (teamId: string, userId: string) => Promise<void>;
  leaveOrganization: (orgId: string) => Promise<void>;
  updateOrganizationName: (orgId: string, name: string) => Promise<void>;
}

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      currentOrganization: null,
      organizations: [],
      loading: false,
      error: null,

      fetchOrganizations: async () => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const response = await axios.get(`${API_BASE_URL}/organizations/`, {
            headers: {
              Authorization: `Bearer ${tokens.access}`,
            },
          });
          
          const orgs = response.data.results || response.data;
          const organizationsArray = Array.isArray(orgs) ? orgs : [];
          
          set({ organizations: organizationsArray, loading: false });
          
          // Set current organization if not set
          if (!get().currentOrganization && organizationsArray.length > 0) {
            get().fetchCurrentOrganization(organizationsArray[0].id);
          }
        } catch (error) {
          console.error('Failed to fetch organizations:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch organizations',
            loading: false 
          });
        }
      },

      fetchCurrentOrganization: async (orgId?: string) => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          let targetOrgId = orgId || get().currentOrganization?.id || get().organizations[0]?.id;
          if (!targetOrgId) {
            // Fetch organizations first
            await get().fetchOrganizations();
            const firstOrg = get().organizations[0];
            if (!firstOrg) {
              set({ loading: false });
              return;
            }
            targetOrgId = firstOrg.id;
          }

          try {
            const response = await axios.get(`${API_BASE_URL}/organizations/${targetOrgId}/`, {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            });
            set({ currentOrganization: response.data, loading: false });
          } catch (innerError: any) {
            if (innerError?.response?.status === 404) {
              // Stale persisted org ID — clear it and load the user's actual orgs
              set({ currentOrganization: null, organizations: [] });
              await get().fetchOrganizations();
            } else {
              throw innerError;
            }
          }
        } catch (error) {
          console.error('Failed to fetch current organization:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch organization',
            loading: false
          });
        }
      },

      setCurrentOrganization: (org: Organization | null) => {
        set({ currentOrganization: org as OrganizationDetail | null });
      },

      switchOrganization: async (orgId: string) => {
        await get().fetchCurrentOrganization(orgId);
      },

      inviteMember: async (orgId: string, email: string, role: 'admin' | 'member' = 'member') => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/invite_member/`,
            { email, role },
            {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          );
          
          // Refresh organization data
          await get().fetchCurrentOrganization(orgId);
        } catch (error) {
          console.error('Failed to invite member:', error);
          throw error;
        }
      },

      cancelInvite: async (orgId: string, inviteId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/cancel_invite/`,
            { invite_id: inviteId },
            {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          );
          
          // Refresh organization data
          await get().fetchCurrentOrganization(orgId);
        } catch (error) {
          console.error('Failed to cancel invite:', error);
          throw error;
        }
      },

      resendInvite: async (orgId: string, inviteId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/resend_invite/`,
            { invite_id: inviteId },
            {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          );
          
          // Refresh organization data
          await get().fetchCurrentOrganization(orgId);
        } catch (error) {
          console.error('Failed to resend invite:', error);
          throw error;
        }
      },

      removeMember: async (orgId: string, userId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/remove_member/`,
            { user_id: userId },
            {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          );
          
          // Refresh organization data
          await get().fetchCurrentOrganization(orgId);
        } catch (error) {
          console.error('Failed to remove member:', error);
          throw error;
        }
      },

      updateMemberRole: async (orgId: string, userId: string, role: 'owner' | 'admin' | 'member') => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/update_member_role/`,
            { user_id: userId, role },
            {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          );
          
          // Refresh organization data
          await get().fetchCurrentOrganization(orgId);
        } catch (error) {
          console.error('Failed to update member role:', error);
          throw error;
        }
      },

      transferOwnership: async (orgId: string, newOwnerId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/transfer_ownership/`,
            { new_owner_id: newOwnerId },
            {
              headers: {
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          );

          await get().fetchCurrentOrganization(orgId);
        } catch (error) {
          console.error('Failed to transfer ownership:', error);
          throw error;
        }
      },

      addTeamAdmin: async (teamId: string, userId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) throw new Error('Not authenticated');

          await axios.post(
            `${API_BASE_URL}/teams/${teamId}/manage_admin/`,
            { user_id: userId, action: 'add' },
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
        } catch (error) {
          console.error('Failed to add team admin:', error);
          throw error;
        }
      },

      removeTeamAdmin: async (teamId: string, userId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) throw new Error('Not authenticated');

          await axios.post(
            `${API_BASE_URL}/teams/${teamId}/manage_admin/`,
            { user_id: userId, action: 'remove' },
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
        } catch (error) {
          console.error('Failed to remove team admin:', error);
          throw error;
        }
      },

      updateOrganizationName: async (orgId: string, name: string) => {
        const tokens = useAuthStore.getState().tokens;
        if (!tokens?.access) throw new Error('Not authenticated');

        const response = await axios.patch(
          `${API_BASE_URL}/organizations/${orgId}/`,
          { name },
          { headers: { Authorization: `Bearer ${tokens.access}` } }
        );

        // Update both the list and the current org in state
        set(state => ({
          organizations: state.organizations.map(o => o.id === orgId ? { ...o, name } : o),
          currentOrganization: state.currentOrganization?.id === orgId
            ? { ...state.currentOrganization, ...response.data }
            : state.currentOrganization,
        }));
      },

      leaveOrganization: async (orgId: string) => {
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) throw new Error('Not authenticated');

          await axios.post(
            `${API_BASE_URL}/organizations/${orgId}/leave_organization/`,
            {},
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );

          // Remove from local state
          const orgs = get().organizations.filter(o => o.id !== orgId);
          const currentOrg = get().currentOrganization;
          set({
            organizations: orgs,
            currentOrganization: currentOrg?.id === orgId ? null : currentOrg,
          });
        } catch (error) {
          console.error('Failed to leave organization:', error);
          throw error;
        }
      },
    }),
    {
      name: 'organization-storage',
      partialize: (state) => ({
        currentOrganization: state.currentOrganization,
        organizations: state.organizations,
      }),
    }
  )
);

